import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GripVertical, Pencil, Plus, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const CATEGORY_LABELS = { product: "Product", architecture: "Architecture", food: "Food", "motion-ai": "Motion / AI" };

const ProjectsAdmin = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragIndex, setDragIndex] = useState(null);

  const load = () => {
    api.get("/projects")
      .then((res) => setProjects(res.data))
      .catch(() => toast.error("Errore nel caricamento dei progetti"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleDelete = async (slug) => {
    if (!window.confirm("Eliminare definitivamente questo progetto?")) return;
    try {
      await api.delete(`/admin/projects/${slug}`);
      toast.success("Progetto eliminato");
      setProjects((prev) => prev.filter((p) => p.slug !== slug));
    } catch {
      toast.error("Eliminazione non riuscita");
    }
  };

  const handleToggleFeatured = async (project) => {
    const next = !project.featured;
    setProjects((prev) => prev.map((p) => (p.slug === project.slug ? { ...p, featured: next } : p)));
    try {
      await api.put(`/admin/projects/${project.slug}`, { ...project, featured: next });
      toast.success(next ? `"${project.title}" ora è in home` : `"${project.title}" rimosso dalla home`);
    } catch {
      setProjects((prev) => prev.map((p) => (p.slug === project.slug ? { ...p, featured: !next } : p)));
      toast.error("Modifica non riuscita. Riprova.");
    }
  };

  const handleDrop = async (targetIndex) => {
    if (dragIndex === null || dragIndex === targetIndex) {
      setDragIndex(null);
      return;
    }
    const next = [...projects];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(targetIndex, 0, moved);
    setProjects(next);
    setDragIndex(null);
    try {
      await api.put("/admin/projects-order", { slugs: next.map((p) => p.slug) });
      toast.success("Ordine aggiornato sul sito");
    } catch {
      toast.error("Ordine non salvato");
    }
  };

  const featuredCount = projects.filter((p) => p.featured).length;

  return (
    <div data-testid="admin-projects-page">
      <div className="flex items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-normal tracking-tight text-bone">Progetti</h1>
          <p className="mt-1 text-sm text-fog">
            Crea, modifica o elimina i case study. Trascina le righe per cambiare l'ordine sul sito. La <Star size={11} className="inline -mt-0.5 text-gold fill-gold" /> oro indica i progetti visibili nella sezione "Lavori selezionati" della home — senza limiti di numero.
          </p>
          <p data-testid="featured-count" className="mt-2 text-[10px] font-mono tracking-[0.25em] text-gold uppercase">{featuredCount} progetti in home</p>
        </div>
        <Link
          to="/admin/progetti/nuovo"
          data-testid="admin-new-project-button"
          className="inline-flex items-center gap-2 px-5 py-3 bg-gold text-night text-xs font-semibold tracking-[0.15em] hover:bg-bone transition-colors duration-300 shrink-0"
        >
          <Plus size={14} /> NUOVO PROGETTO
        </Link>
      </div>

      {loading && <p className="text-xs font-mono tracking-[0.25em] text-fog">CARICAMENTO…</p>}

      <div className="space-y-3">
        {projects.map((project, i) => (
          <div
            key={project.slug}
            data-testid={`admin-project-row-${project.slug}`}
            draggable
            onDragStart={() => setDragIndex(i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => handleDrop(i)}
            className={`flex items-center gap-4 border p-3 bg-surface transition-colors duration-200 ${
              dragIndex === i ? "border-gold opacity-60" : "border-line"
            }`}
          >
            <span data-testid={`drag-handle-${project.slug}`} className="cursor-grab text-fog/50 hover:text-fog shrink-0" aria-label="Trascina per riordinare">
              <GripVertical size={16} />
            </span>
            <img src={project.hero} alt={project.title} className="w-28 h-16 object-cover bg-coal shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-bone truncate">{project.title}</p>
              <p className="mt-1 text-[10px] font-mono tracking-[0.2em] text-fog uppercase">
                {String(i + 1).padStart(2, "0")} · {CATEGORY_LABELS[project.category] || project.category} — {project.year}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => handleToggleFeatured(project)}
                data-testid={`admin-featured-project-${project.slug}`}
                title={project.featured ? "Togli dalla home" : "Mostra in home"}
                aria-pressed={project.featured}
                className={`inline-flex items-center gap-2 px-4 py-2.5 border text-[10px] tracking-[0.15em] transition-[color,border-color] duration-300 ${
                  project.featured
                    ? "border-gold text-gold"
                    : "border-line text-fog hover:text-bone hover:border-fog"
                }`}
              >
                <Star size={12} className={project.featured ? "fill-gold" : ""} /> HOME
              </button>
              <Link
                to={`/admin/progetti/${project.slug}`}
                data-testid={`admin-edit-project-${project.slug}`}
                className="inline-flex items-center gap-2 px-4 py-2.5 border border-line text-fog text-[10px] tracking-[0.15em] hover:text-bone hover:border-fog transition-[color,border-color] duration-300"
              >
                <Pencil size={12} /> MODIFICA
              </Link>
              <button
                onClick={() => handleDelete(project.slug)}
                data-testid={`admin-delete-project-${project.slug}`}
                className="inline-flex items-center gap-2 px-4 py-2.5 border border-line text-fog text-[10px] tracking-[0.15em] hover:text-red-400 hover:border-red-400 transition-[color,border-color] duration-300"
              >
                <Trash2 size={12} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProjectsAdmin;
